import { useState } from "react";
import Dialog from "components/Overlays/ResponsiveDialog";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import PhysicalBookFields from "../PhysicalBookFields";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { IoIosArrowForward } from "react-icons/io";
import useUpdatePhysicalBook from "api/BookResource/useUpdatePhysicalBook";
import { MAX_IMAGE_SIZE } from "utils";
import { HiPencil } from "react-icons/hi";
import useFetchCategories from "api/CategoryResource/useFetchCategories";
import useGenres from "api/GenreResource/useGenres";

const UpdatePhysicalBook = ({ book }) => {
	const [visible, setVisible] = useState(false);

	// Fetch data for dropdowns
	const { data: categories, isLoading: isCategoriesLoading } =
		useFetchCategories("yes");
	const { data: genres, isLoading: isGenresLoading } = useGenres("yes");

	const initialValues = {
		title: book?.title || "",
		author: book?.author || "",
		isbn: book?.isbn || "",
		description: book?.description || "",
		category_id: book?.category?.id || "",
		genres: book?.genres?.[0]?.id || "",
		physical_usd_price: book?.physical_usd_price ?? "",
		book_cover: null,
	};

	const validate = Yup.object().shape({
		title: Yup.string().required("Book title is required"),
		author: Yup.string().required("Author is required"),
		category_id: Yup.number().required("Category is required"),
		// genres: Yup.string().required("Genre is required"),
		physical_usd_price: Yup.number()
			.typeError("Price must be a number")
			.min(0, "Price must be greater than or equal to 0")
			.required("Price is required"),
		book_cover: Yup.mixed()
			.nullable()
			.test({
				name: "fileSize",
				test: (value) => !value || value.size / 1024 / 1024 <= MAX_IMAGE_SIZE,
				message: `Image size should not exceed ${MAX_IMAGE_SIZE}MB`,
			})
			.test({
				name: "mimeType",
				test: (value) =>
					!value ||
					["image/jpeg", "image/png", "image/svg+xml", "image/webp"].includes(
						value.type
					),
				message:
					"Only images with these file extensions are allowed: jpeg, jpg, png, svg, webp",
			}),
	});

	const { mutateAsync, isLoading } = useUpdatePhysicalBook(book?.id);

	const handleSubmit = async (data) => {
		await mutateAsync(data);
		setVisible(false);
	};

	// Prepare options for dropdowns
	const categoryOptions =
		categories?.data?.map((category) => ({
			label: category.name,
			value: category.id,
		})) || [];

	const genreOptions =
		genres?.data?.data?.map((genre) => ({
			label: genre.name,
			value: genre.id,
		})) || [];

	return (
		<>
			<button
				type="button"
				onClick={() => setVisible(true)}
				className="physical-book-edit"
				aria-label={`Edit ${book?.title || "physical book"}`}
				title="Update book"
			>
				<HiPencil size={16} aria-hidden="true" /><span>Edit</span>
			</button>

			<Dialog
				visible={visible}
				onHide={() => setVisible(false)}
				header="Edit physical book"
				description="Update this print edition’s details, pricing and cover."
				style={{ minWidth: "500px", maxWidth: "600px" }}
				// modal
				className="font-sans physical-book-dialog"
				modal blockScroll
			>
				<Formik
					initialValues={initialValues}
					validationSchema={validate}
					onSubmit={handleSubmit}
				>
					{({ setFieldValue, values, errors, touched }) => (
						<Form className="akello-sectioned-form">
							<PhysicalBookFields values={values} errors={errors} touched={touched} setFieldValue={setFieldValue}
								categoryOptions={categoryOptions} genreOptions={genreOptions}
								isCategoriesLoading={isCategoriesLoading} isGenresLoading={isGenresLoading} currentCover={book?.book_cover?.url} />
							<div className="akello-form-actions is-sticky">
								<button
									type="button"
									onClick={() => setVisible(false)}
									className="akello-action"
								>
									Cancel
								</button>
								<button
									type="submit"
									disabled={isLoading}
									className="akello-action akello-action-primary"
								>
									{isLoading ? (
										<>
											<AiOutlineLoading3Quarters
												size={16}
												className="animate-spin"
											/>
											Updating...
										</>
									) : (
										<>
											Update Book
											<IoIosArrowForward size={16} />
										</>
									)}
								</button>
							</div>
						</Form>
					)}
				</Formik>
			</Dialog>
		</>
	);
};

export default UpdatePhysicalBook;
